/**
 * Quarantine.js — Módulo Quarentena (Onda 35)
 * CI Sistemas de Informática Ltda. — FastALPR Parking
 *
 * Veículos que saíram sem serem lidos pela câmera de saída, com o contador de
 * ocorrências e as evidências de cada uma. A quarentena descreve um estado
 * observado, não uma acusação: uma ocorrência isolada quase sempre é falha de
 * leitura. Nada aqui restringe acesso; o status é decisão do operador.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';
import '../styles/Quarantine.css';

const STATUS_LABEL = {
  em_observacao: 'Em observação',
  revisado: 'Revisado',
  liberado: 'Liberado',
};

function fmtDateTime(dt) { return dt ? new Date(dt).toLocaleString('pt-BR') : '—'; }
function fmtMoney(v) { return `R$ ${Number(v || 0).toFixed(2)}`; }
function evidenceUrl(p, token) { return p ? `/api/evidence/${p}?token=${encodeURIComponent(token || '')}` : null; }
function engineUrl(cropPath, token) {
  if (!cropPath) return null;
  return `/api/ocr-quality/file/${cropPath.replace(/\.crop\.png$/, '.jpg')}?token=${encodeURIComponent(token || '')}`;
}

// ─── Modal de evidências da placa ───────────────────────────────────────────
function EventsModal({ plate, token, onClose, onReviewed, canAct }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState('');
  const [zoom, setZoom] = useState(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') (zoom ? setZoom(null) : onClose()); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, zoom]);

  useEffect(() => {
    let vivo = true;
    api.get(`/api/quarantine/${plate}/events`)
      .then(r => { if (vivo) setData(r.data); })
      .catch(() => { if (vivo) setData({ events: [] }); });
    return () => { vivo = false; };
  }, [plate]);

  const marcar = async (status) => {
    setBusy(true);
    try {
      await api.post(`/api/quarantine/${plate}/status`, { status, notes: notes || null });
      toast.success(`${plate} marcada como ${STATUS_LABEL[status].toLowerCase()}`);
      onReviewed();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Falha ao registrar a revisão');
    } finally { setBusy(false); }
  };

  const eventos = data?.events || [];
  return (
    <div className="qua-modal-backdrop" onClick={onClose} role="presentation">
      <div className="qua-modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={`Ocorrências de ${plate}`}>
        <header className="qua-modal-head">
          <h3>Ocorrências de <span className="qua-plate">{plate}</span></h3>
          <button type="button" className="qua-modal-close" onClick={onClose} aria-label="Fechar">×</button>
        </header>

        <div className="qua-modal-body">
          {!data && <div className="qua-empty">Carregando…</div>}
          {data && eventos.length === 0 && <div className="qua-empty">Sem ocorrências registradas.</div>}
          {eventos.map(ev => {
            const img = evidenceUrl(ev.image_path, token) || engineUrl(ev.plate_crop_path, token);
            const entrada = evidenceUrl(ev.entry_image_path, token);
            return (
              <article key={ev.id} className="qua-event">
                <div className="qua-event-head">
                  <span className={`qua-badge ${ev.kind === 'carona' ? 'bad' : 'warn'}`}>
                    {ev.kind === 'carona' ? 'saiu junto com outro' : 'saída sem leitura'}
                  </span>
                  {/* O horário vem do banco, não do carimbo desenhado na imagem: evidências
                      anteriores a 06/09/2026 têm o carimbo em UTC, três horas à frente. */}
                  <span className="qua-event-when">{fmtDateTime(ev.detected_at)}</span>
                  {ev.camera_name && <span className="qua-muted">· {ev.camera_name}</span>}
                </div>
                <div className="qua-muted">
                  {ev.ticket_id ? <>Ticket #{ev.ticket_id}</> : 'Sem ticket'}
                  {ev.entry_time && <> · entrada {fmtDateTime(ev.entry_time)}</>}
                  {ev.duration_minutes != null && <> · {ev.duration_minutes} min</>}
                  {ev.amount_due != null && <> · {fmtMoney(ev.amount_due)}</>}
                  {ev.auto_closed ? <> · baixa automática</> : <> · sem baixa</>}
                </div>
                {ev.companion_plate && (
                  <div className="qua-muted">
                    {ev.companion_plate} foi registrado saindo {Math.abs(ev.companion_gap_seconds || 0)} s antes ou depois
                  </div>
                )}
                <div className="qua-event-shots">
                  {entrada && (
                    <button type="button" className="qua-shot" onClick={() => setZoom({ url: entrada, label: `Entrada do ticket #${ev.ticket_id}`, when: ev.entry_time })}>
                      <img src={entrada} alt="Entrada" loading="lazy" /><span>Entrada</span>
                    </button>
                  )}
                  {img ? (
                    <button type="button" className="qua-shot" onClick={() => setZoom({ url: img, label: `${plate} saindo pela zona de observação`, when: ev.detected_at })}>
                      <img src={img} alt="Saída observada" loading="lazy" /><span>Saída observada</span>
                    </button>
                  ) : <div className="qua-shot qua-shot-empty">sem imagem</div>}
                </div>
              </article>
            );
          })}
        </div>

        {canAct && (
          <footer className="qua-modal-foot">
            <input className="qua-notes" placeholder="Observação do operador (opcional)" value={notes}
              maxLength={500} onChange={e => setNotes(e.target.value)} />
            <button className="btn btn-sm btn-secondary" disabled={busy} onClick={() => marcar('revisado')}>Marcar revisado</button>
            <button className="btn btn-sm btn-secondary" disabled={busy} onClick={() => marcar('liberado')}>Liberar</button>
            <button className="btn btn-sm btn-secondary" onClick={onClose}>Fechar</button>
          </footer>
        )}

        {zoom && (
          <div className="qua-zoom" onClick={() => setZoom(null)} role="presentation">
            <figure onClick={e => e.stopPropagation()}>
              <img src={zoom.url} alt={zoom.label} />
              <figcaption>
                {zoom.label} · {fmtDateTime(zoom.when)}
                <button type="button" className="btn btn-sm btn-secondary" onClick={() => setZoom(null)}>Fechar</button>
              </figcaption>
            </figure>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Quarantine() {
  const { can } = useAuth();
  const token = localStorage.getItem('token');
  const canAct = can('quarantine_review');
  const [days, setDays] = useState(90);
  const [status, setStatus] = useState('');
  const [minOcc, setMinOcc] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ days: String(days), min_occurrences: String(minOcc) });
      if (status) p.append('status', status);
      const r = await api.get(`/api/quarantine?${p.toString()}`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erro ao carregar a quarentena');
    } finally { setLoading(false); }
  }, [days, status, minOcc]);
  useEffect(() => { load(); }, [load]);

  const items = data?.items || [];
  const counts = data?.counts || {};

  return (
    <div className="qua-page">
      <div className="page-header">
        <div>
          <h1>Quarentena</h1>
          <p className="page-subtitle">
            Veículos reconhecidos saindo pela zona de observação da câmera de entrada, sem leitura na câmera de saída.
            Uma ocorrência isolada costuma ser falha de leitura; o que importa aqui é a reincidência.
          </p>
        </div>
        <div className="qua-controls">
          {[30, 90, 365].map(d => (
            <button key={d} className={`btn btn-sm ${days === d ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setDays(d)}>{d} dias</button>
          ))}
        </div>
      </div>

      <div className="qua-cards">
        <div className="qua-card"><span>Placas</span><strong>{counts.total || 0}</strong></div>
        <div className="qua-card"><span>Com carona</span><strong>{counts.com_carona || 0}</strong></div>
        <div className="qua-card"><span>Três ou mais ocorrências</span><strong>{counts.reincidentes || 0}</strong></div>
        <div className="qua-card"><span>Em observação</span><strong>{counts.em_observacao || 0}</strong></div>
      </div>

      <div className="qua-filters">
        <select value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">Todos os status</option>
          {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={minOcc} onChange={e => setMinOcc(Number(e.target.value))}>
          {[1, 2, 3, 5].map(n => <option key={n} value={n}>{n}+ ocorrência{n > 1 ? 's' : ''}</option>)}
        </select>
        <button className="btn btn-secondary btn-sm" onClick={load} disabled={loading}>{loading ? 'Carregando…' : 'Atualizar'}</button>
      </div>

      <div className="qua-table-wrap">
        <table className="qua-table">
          <thead>
            <tr>
              <th>Placa</th><th>Ocorrências</th><th>Com carona</th><th>Primeira</th><th>Última</th>
              <th>Valor apurado</th><th>Tickets</th><th>Status</th><th></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && !loading && (
              <tr><td colSpan={9} className="qua-empty">Nenhum veículo na quarentena para os filtros escolhidos.</td></tr>
            )}
            {items.map(v => (
              <tr key={v.plate} className={Number(v.occurrences) >= 3 ? 'qua-row-hot' : ''}>
                <td className="qua-plate">{v.plate}</td>
                <td><strong>{v.occurrences}</strong></td>
                <td>{v.carona_occurrences || 0}</td>
                <td className="qua-nowrap">{fmtDateTime(v.first_seen_at)}</td>
                <td className="qua-nowrap">{fmtDateTime(v.last_seen_at)}</td>
                <td>{fmtMoney(v.total_amount)}</td>
                <td>{v.tickets_historicos}</td>
                <td><span className={`qua-badge status-${v.status}`}>{STATUS_LABEL[v.status] || v.status}</span></td>
                <td><button className="btn btn-sm btn-secondary" onClick={() => setSelected(v.plate)}>Evidências</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <EventsModal plate={selected} token={token} canAct={canAct}
          onClose={() => setSelected(null)} onReviewed={load} />
      )}
    </div>
  );
}
